import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { client } from "../../lib/api";

export default function TermsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  async function handleAgree() {
    setLoading(true);
    setError(null);
    try {
      await client.users.patchUsersProfile({
        requestBody: { termsAcceptedAt: new Date().toISOString() },
      });
      await queryClient.refetchQueries({ queryKey: ["user-profile"] });
      navigate("/", { replace: true });
    } catch {
      if (mountedRef.current) {
        setError("Something went wrong. Please try again.");
        setLoading(false);
      }
    }
  }

  return (
    <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-bg-primary rounded-xl border border-border p-8 space-y-6">
        <h1 className="text-2xl font-bold text-text-primary">
          Terms and Conditions
        </h1>

        {/* TODO: Review with legal before shipping */}
        <div className="relative">
          <div
            className="space-y-4 text-text-secondary text-sm leading-relaxed overflow-y-auto max-h-96"
            tabIndex={0}
          >
            <p>
              Welcome to TrainRFlo. By using this platform you agree to the
              following terms. Please read them carefully before continuing.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              1. What TrainRFlo Is
            </h2>
            <p>
              TrainRFlo is an AI-assisted fitness coaching platform that helps
              trainers deliver personalised exercise programs and supports
              customers in tracking their movement and rehabilitation progress.
              The platform is a fitness tool only — it is not a medical service
              and does not provide diagnosis, treatment, or clinical care.
              Nothing on TrainRFlo should be treated as a substitute for
              professional medical advice.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              2. Data We Collect
            </h2>
            <p>
              To provide the service we collect your account information (name
              and email address), exercise completion records, pain scores you
              self-report, notes you or your trainer enter, and the content of
              your AI coaching conversations. This data is stored securely and
              associated only with your account.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              3. How We Use Your Data
            </h2>
            <p>
              Your data is used to personalise your coaching experience and to
              improve the TrainRFlo service over time. We do not sell your
              personal data to third parties. Aggregated, anonymised data may be
              used internally to improve AI responses and platform features.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              4. AI Disclaimer
            </h2>
            <p>
              AI-generated responses on this platform are informational and
              motivational in nature — they are not medical advice. Before
              starting any new exercise program, and especially if you have an
              existing injury, medical condition, or are recovering from
              surgery, you must consult a qualified physician or
              physiotherapist. TrainRFlo accepts no liability for outcomes
              arising from reliance on AI-generated content.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              5. Your Responsibilities
            </h2>
            <p>
              You agree to provide accurate information about yourself,
              including any injuries or conditions that may be relevant to your
              exercise program. You must not use TrainRFlo as a replacement for
              medical care or professional clinical advice. You are responsible
              for keeping your account credentials confidential.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              6. Deleting Your Account
            </h2>
            <p>
              You can delete your account and all associated data at any time
              from your account settings. Deletion is permanent — we do not
              retain your personal information after you request removal.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              7. Age Requirement
            </h2>
            <p>
              You must be at least 16 years old to create an account and use
              TrainRFlo. By accepting these terms you confirm that you meet this
              age requirement.
            </p>

            <h2 className="text-base font-semibold text-text-primary">
              8. Governing Law
            </h2>
            <p>
              These terms are governed by the laws of [Jurisdiction]. Details to
              be confirmed. If any provision of these terms is found to be
              unenforceable, the remaining provisions will continue in full
              force.
            </p>
          </div>
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-bg-primary to-transparent rounded-b-lg" />
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-action-primary cursor-pointer"
          />
          <span className="text-sm text-text-secondary">
            I have read and agree to the Terms and Conditions above.
          </span>
        </label>

        <button
          onClick={handleAgree}
          disabled={!agreed || loading}
          className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Please wait…
            </span>
          ) : (
            "I Agree"
          )}
        </button>
      </div>
    </div>
  );
}
